// Helpers for deep-linking to a specific comment via URL hash
// (e.g. notification emails → #site_comment_123).

export const SITE_COMMENT_HASH_RE = /^#?site_comment_(\d+)$/

// Comments are cursor-paginated 15 at a time, so a deep-linked comment can sit
// several pages down. 20 pages covers ~300 root comments, which is well past any
// real thread — past that we stop rather than keep hammering the server.
export const MAX_COMMENT_PAGE_LOADS = 20

/**
 * Parse a location hash (or id string) into a comment DOM id.
 * @param {string|null|undefined} hash
 * @returns {string|null} e.g. "site_comment_123" or null
 */
export function parseSiteCommentHash(hash) {
  if (!hash || typeof hash !== 'string') return null
  const match = hash.trim().match(SITE_COMMENT_HASH_RE)
  return match ? `site_comment_${match[1]}` : null
}

/**
 * Load pages until `find()` reports the target is there. Stops as soon as it is,
 * when `loadNext()` resolves false (no more pages / load failed), or at maxLoads.
 * @returns {Promise<boolean>} whether the target was found
 */
export async function seekAcrossPages({
  find,
  loadNext,
  maxLoads = MAX_COMMENT_PAGE_LOADS,
}) {
  for (let loads = 0; loads <= maxLoads; loads++) {
    if (find()) return true
    if (loads === maxLoads) break
    if (!(await loadNext())) break
  }

  return false
}

/**
 * Show the collapsed replies in a replies container and drop its "View N more replies"
 * button. Only looks at direct children: nested reply lists keep their own collapsing.
 * @param {Element|null|undefined} repliesContainer
 * @returns {Element|null} the first reply revealed
 */
export function revealCollapsedReplies(repliesContainer) {
  let firstRevealed = null

  for (const child of Array.from(repliesContainer?.children || [])) {
    if (child.classList.contains('js-collapsed-reply')) {
      child.classList.remove('hidden', 'js-collapsed-reply')
      firstRevealed ||= child
    } else if (child.classList.contains('js-show-collapsed-replies')) {
      child.remove()
    }
  }

  return firstRevealed
}

/**
 * Make a (possibly collapsed) comment visible, e.g. before scrolling to a deep-linked one,
 * by revealing every collapsed reply list it sits in.
 * @param {Element} el
 */
export function revealCollapsedAncestors(el) {
  for (let node = el; node; node = node.parentElement) {
    if (node.classList?.contains('js-collapsed-reply')) {
      revealCollapsedReplies(node.parentElement)
    }
  }
}
